import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import RunEventDistanceForm from './run-event-distance-form';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { RunEventDistance } from '@/models/run-event-distance.model';
import { getRunEventDistance } from '@/services/run-event-distance.service';
import RunEventDistanceActions from './run-event-distance-actions';
import PageHeader from '@/components/PageHeader';
import EventRecordForm from '@/pages/event-record/event-record-form';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect} >
      <Nav.Item eventKey="distance-form">Run Event Distance</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="distance-record-provider">Record Provider</Nav.Item>
      <Nav.Item eventKey="distance-actions">Actions</Nav.Item>
    </Nav>
  );
};

const Page = () => {
  const runEventId = useLocation()?.state?.runEventId;
  const requestedTab = useLocation()?.state?.tab;  
  
  const [activeTab, setActiveTab] = useState('distance-form');
  const [formObject, setFormObject] = useState<RunEventDistance|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;

  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<RunEventDistance> = await getRunEventDistance(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Run Event Distance: ${errorModel?.errorMessage}`);
        nav('/run-events/distances');
      }
      finally {
        setLoading(false);
      }
    }
  };

  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit ? `Edit Distance` : 'New Distance'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='/run-events'>Run Events</Breadcrumb.Item>
            <Breadcrumb.Item href='#' onClick={() => nav(`/run-events/${formObject?.runEventId ?? runEventId}`, {state:{tab: "run-event-distances"}})} >Distances</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" 
        active={activeTab} 
        onSelect={key => setActiveTab(key)} 
        newMode={pageState == PageState.New} 
      />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'distance-form' && <RunEventDistanceForm runEventId={runEventId} formObject={formObject} pageState={pageState}/>}
          {activeTab == 'distance-record-provider' && id && <EventRecordForm runEventDistanceId={id}/>}
          {activeTab == 'distance-actions' && <RunEventDistanceActions formObject={formObject} fetchData={fetchData}/>}
        </>
      }
    </Panel>
  );
};

export default Page;
